/**
 * ZELIVIX Luxury Experience & Animations Engine
 * Interactive PDP personalization studio, live preview, scroll reveals,
 * micro-interactions, brand safeguards, and performance optimizations.
 * Lightweight, zero dependencies, defensive error handling.
 */

(function () {
  'use strict';

  // Guard against environments where window or document are unavailable
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Check user preference for reduced motion
  var prefersReducedMotion = false;
  try {
    prefersReducedMotion = Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch (e) {
    prefersReducedMotion = false;
  }

  // Check if pointer is mouse/hover capable (prevents touch device bugs)
  var hasHover = false;
  try {
    hasHover = Boolean(
      window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
  } catch (e) {
    hasHover = false;
  }

  // Persistent reference for urgency countdown interval
  var urgencyIntervalId = null;

  // Single shared IntersectionObserver for scroll reveals
  var scrollObserver = null;

  /* ─── Helper: Safe Debounce ─── */
  function debounce(fn, wait) {
    var timeout;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* ─── 1. Brand Name Safeguard (Replaces "My Store 8" Without Mutating Container HTML) ─── */
  function initBrandNameScrubber() {
    try {
      // Fix document title if lingering
      if (document.title && (document.title.indexOf('My Store 8') !== -1 || document.title.indexOf('ZELIVIX') !== -1)) {
        document.title = document.title.replace(/My Store 8/g, 'ZELVIX').replace(/ZELIVIX/g, 'ZELVIX');
      }

      // Safely replace text nodes without ever blowing away innerHTML or event listeners
      var targets = document.querySelectorAll(
        '.header-logo, [data-testid="header-logo"], .footer__copyright, .footer__heading, .header__heading, h1, h2, h3, h4, p, a, span'
      );

      for (var i = 0; i < targets.length; i++) {
        var el = targets[i];
        // Only inspect leaf or small elements to avoid expensive subtree traversals
        if (el && el.children.length === 0 && el.textContent) {
          if (el.textContent.indexOf('My Store 8') !== -1 || el.textContent.indexOf('ZELIVIX') !== -1) {
            el.textContent = el.textContent.replace(/My Store 8/g, 'ZELVIX').replace(/ZELIVIX/g, 'ZELVIX');
          }
        }
      }
    } catch (err) {
      // Non-critical: fail silently
    }
  }

  /* ─── 2. Interactive PDP Personalization Studio ─── */
  function initLivePersonalization() {
    try {
      var input = document.getElementById('zelvix-engrave-text');
      var preview = document.getElementById('zelvix-live-name');
      var charCounter = document.getElementById('zelvix-char-current');
      var fontInput = document.getElementById('zelvix-font-input');
      var fontBtns = document.querySelectorAll('.zelvix-font-btn');

      // Sync function for live engraving preview & character counter
      function updateEngravingPreview() {
        if (!input) return;
        var rawVal = input.value || '';
        var trimmed = rawVal.trim();

        if (preview) {
          preview.textContent = trimmed.length > 0 ? trimmed : 'Your Name';
        }
        if (charCounter) {
          charCounter.textContent = String(rawVal.length);
        }
      }

      // Live Engraving Input binding
      if (input && !input.dataset.zelvixBound) {
        input.dataset.zelvixBound = 'true';

        input.addEventListener('input', updateEngravingPreview);
        input.addEventListener('keyup', updateEngravingPreview);
        input.addEventListener('change', updateEngravingPreview);
        input.addEventListener('paste', function () {
          setTimeout(updateEngravingPreview, 10);
        });

        // Initial sync on page load / bfcache
        updateEngravingPreview();
      }

      // Font Style Selector
      if (fontBtns && fontBtns.length > 0) {
        for (var f = 0; f < fontBtns.length; f++) {
          (function (btn) {
            if (btn.dataset.zelvixBound) return;
            btn.dataset.zelvixBound = 'true';

            btn.addEventListener('click', function (e) {
              e.preventDefault();
              for (var j = 0; j < fontBtns.length; j++) {
                fontBtns[j].classList.remove('active');
              }
              btn.classList.add('active');

              var fontType = btn.getAttribute('data-font') || 'script';
              var fontName = btn.getAttribute('data-font-name') || 'Romantic Script';

              if (fontInput) {
                fontInput.value = fontName;
              }

              if (preview) {
                preview.classList.remove('font-script', 'font-serif', 'font-modern');
                preview.classList.add('font-' + fontType);
              }
            });
          })(fontBtns[f]);
        }

        // Sync initial active font class to preview
        var activeBtn = document.querySelector('.zelvix-font-btn.active');
        if (activeBtn && preview) {
          var initialFont = activeBtn.getAttribute('data-font') || 'script';
          preview.classList.remove('font-script', 'font-serif', 'font-modern');
          preview.classList.add('font-' + initialFont);
        }
      }
    } catch (err) {
      // PDP suite initialized safely
    }
  }

  /* ─── 3. Urgency Countdown Timer (Persistent Across Page Navigation) ─── */
  function initUrgencyTimer() {
    try {
      var timerDisplay = document.getElementById('zelvix-timer-display');
      if (!timerDisplay) {
        if (urgencyIntervalId) {
          clearInterval(urgencyIntervalId);
          urgencyIntervalId = null;
        }
        return;
      }

      // Clean up previous interval before creating a new one
      if (urgencyIntervalId) {
        clearInterval(urgencyIntervalId);
        urgencyIntervalId = null;
      }

      // Store countdown end timestamp in sessionStorage so timer persists smoothly
      var storageKey = 'zelvix_urgency_expiry';
      var now = Date.now();
      var storedExpiry = null;

      try {
        storedExpiry = parseInt(sessionStorage.getItem(storageKey), 10);
      } catch (e) {
        storedExpiry = null;
      }

      // Default duration: 3 hours, 24 minutes, 18 seconds (12258 seconds)
      var durationMs = (3 * 3600 + 24 * 60 + 18) * 1000;

      if (!storedExpiry || isNaN(storedExpiry) || storedExpiry <= now) {
        storedExpiry = now + durationMs;
        try {
          sessionStorage.setItem(storageKey, String(storedExpiry));
        } catch (e) {
          // Ignore private browsing storage quota errors
        }
      }

      function updateDisplay() {
        var currentNow = Date.now();
        var remainingSeconds = Math.max(0, Math.floor((storedExpiry - currentNow) / 1000));

        // If time ran out, reset by adding 4 hours
        if (remainingSeconds <= 0) {
          storedExpiry = Date.now() + 4 * 3600 * 1000;
          try {
            sessionStorage.setItem(storageKey, String(storedExpiry));
          } catch (e) {}
          remainingSeconds = 4 * 3600;
        }

        var hours = Math.floor(remainingSeconds / 3600);
        var minutes = Math.floor((remainingSeconds % 3600) / 60);
        var seconds = remainingSeconds % 60;

        timerDisplay.textContent =
          hours + 'h ' +
          (minutes < 10 ? '0' : '') + minutes + 'm ' +
          (seconds < 10 ? '0' : '') + seconds + 's';
      }

      updateDisplay();
      urgencyIntervalId = setInterval(updateDisplay, 1000);
    } catch (err) {
      // Non-critical
    }
  }

  /* ─── 4. Non-Destructive Scroll Reveals (Only for Custom ZELIVIX Components) ─── */
  function initScrollReveals() {
    try {
      if (prefersReducedMotion) return;
      if (!('IntersectionObserver' in window)) return;

      // Only target bespoke ZELIVIX components.
      // NEVER target cards or containers that should remain always visible.
      var selectors = [
        '.zelvix-quality-item',
        '.zelvix-customizer-box',
        '.zelvix-urgency-banner'
      ];

      var elements = document.querySelectorAll(selectors.join(','));
      if (!elements.length) return;

      // Create single shared observer if not already instantiated
      if (!scrollObserver) {
        scrollObserver = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              var entry = entries[i];
              if (entry.isIntersecting) {
                entry.target.classList.add('zelvix-visible');
                scrollObserver.unobserve(entry.target);
              }
            }
          },
          { threshold: 0.08, rootMargin: '0px 0px 60px 0px' }
        );
      }

      var windowHeight = window.innerHeight || document.documentElement.clientHeight;

      for (var j = 0; j < elements.length; j++) {
        var el = elements[j];
        if (el.dataset.zelvixRevealBound) continue;
        el.dataset.zelvixRevealBound = 'true';

        var rect = el.getBoundingClientRect();
        // If element is already in the viewport on load, show it immediately without animation
        if (rect.top < windowHeight && rect.bottom > 0) {
          el.classList.add('zelvix-visible');
        } else {
          el.setAttribute('data-zelvix-animate', 'fade-up');
          scrollObserver.observe(el);
        }
      }
    } catch (err) {
      // Non-critical
    }
  }

  /* ─── 5. Subtle Card Tilt Effect (Safe & Top-Level Only) ─── */
  function initCardTilt() {
    try {
      if (prefersReducedMotion || !hasHover || window.innerWidth < 990) return;

      // Strictly target top-level card elements inside grid items.
      // DO NOT use wildcard [class*="product-card"] which breaks 7 nested elements!
      var cards = document.querySelectorAll(
        '.product-grid__item > product-card, .resource-list__item > product-card'
      );

      for (var i = 0; i < cards.length; i++) {
        (function (card) {
          if (card.dataset.zelvixTiltBound) return;
          card.dataset.zelvixTiltBound = 'true';

          var ticking = false;

          function onMouseMove(e) {
            if (ticking) return;
            ticking = true;

            window.requestAnimationFrame(function () {
              var rect = card.getBoundingClientRect();
              var x = e.clientX - rect.left;
              var y = e.clientY - rect.top;
              var centerX = rect.width / 2;
              var centerY = rect.height / 2;
              var rotateX = ((y - centerY) / centerY) * -4;
              var rotateY = ((x - centerX) / centerX) * 4;

              card.style.transform =
                'perspective(800px) rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) + 'deg) translateY(-3px)';
              card.style.transition = 'transform 0.1s ease-out';
              ticking = false;
            });
          }

          function onMouseLeave() {
            card.style.transform = '';
            card.style.transition = 'transform 0.35s ease-out';
          }

          card.addEventListener('mousemove', onMouseMove, { passive: true });
          card.addEventListener('mouseleave', onMouseLeave, { passive: true });
        })(cards[i]);
      }
    } catch (err) {
      // Non-critical
    }
  }

  /* ─── 6. Header Scroll Transition (Global, Single Listener) ─── */
  var headerScrollInitialized = false;

  function initHeaderScroll() {
    try {
      if (headerScrollInitialized) return;
      headerScrollInitialized = true;

      var ticking = false;
      function checkScroll() {
        if (!ticking) {
          window.requestAnimationFrame(function () {
            var header = document.getElementById('header-component') || document.querySelector('header-component');
            if (header) {
              var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
              if (scrollTop > 40) {
                header.classList.add('zelvix-scrolled');
              } else {
                header.classList.remove('zelvix-scrolled');
              }
            }
            ticking = false;
          });
          ticking = true;
        }
      }

      window.addEventListener('scroll', checkScroll, { passive: true });
      checkScroll();
    } catch (err) {
      // Non-critical
    }
  }

  /* ─── 7. Floating Heart & Sparkle Particles (Hero Atmosphere) ─── */
  function initParticles() {
    try {
      if (prefersReducedMotion || window.innerWidth < 768) return;

      var hero = document.querySelector('.hero');
      if (!hero || hero.querySelector('.zelvix-particles')) return;

      var container = document.createElement('div');
      container.className = 'zelvix-particles';
      container.setAttribute('aria-hidden', 'true');

      var particles = ['✦', '♡', '✧', '❋', '♡', '✦', '✧', '♡'];
      for (var i = 0; i < particles.length; i++) {
        var particle = document.createElement('span');
        particle.className = 'zelvix-particle';
        particle.textContent = particles[i];
        particle.style.left = (Math.random() * 92 + 4).toFixed(1) + '%';
        particle.style.animationDelay = (Math.random() * 6).toFixed(1) + 's';
        particle.style.animationDuration = (8 + Math.random() * 4).toFixed(1) + 's';
        particle.style.fontSize = (12 + Math.random() * 10).toFixed(0) + 'px';
        particle.style.color = 'rgba(196, 145, 123, 0.45)';
        container.appendChild(particle);
      }

      hero.style.position = 'relative';
      hero.appendChild(container);
    } catch (err) {
      // Non-critical
    }
  }

  /* ─── 8. Apple-Style Pinned Scroll Story Engine ─── */
  /* This theme uses .page-wrapper as the scroll container (body is overflow:hidden). */
  /* We use position:sticky inside it (works with overflow-y:auto parent). */
  function initScrollStory() {
    try {
      var track = document.getElementById('zelvix-scroll-story-track');
      if (!track) return;
      if (track.dataset.zelvixStoryBound) return;
      track.dataset.zelvixStoryBound = 'true';

      var panel = track.querySelector('.zelvix-scroll-story-sticky');
      var cards = Array.prototype.slice.call(track.querySelectorAll('.zelvix-story-card'));
      var dots  = Array.prototype.slice.call(track.querySelectorAll('.zelvix-story-dot'));
      var fill  = track.querySelector('.zelvix-story-progress-fill');
      var hint  = track.querySelector('.zelvix-story-scroll-hint');

      if (!panel || cards.length === 0) return;

      // ── Unlock overflow on all intermediate ancestor elements ──
      // Position:sticky fails if ANY ancestor has overflow hidden/clip or contain: paint
      var curr = track.parentElement;
      while (curr && curr !== document.body && curr !== document.documentElement) {
        if (curr.classList && curr.classList.contains('page-wrapper')) break;
        curr.style.overflow = 'visible';
        curr.style.contain = 'none';
        curr = curr.parentElement;
      }

      var CARD_COUNT = cards.length;
      var TRACK_HEIGHT_VH = CARD_COUNT + 1; // 500vh

      // ── Set track height to create scroll distance ──
      track.style.height = (TRACK_HEIGHT_VH * 100) + 'vh';
      track.style.minHeight = (TRACK_HEIGHT_VH * 100) + 'vh';

      // ── Make sticky panel ──
      panel.style.position = '-webkit-sticky';
      panel.style.position = 'sticky';
      panel.style.top = '0';
      if (window.innerWidth >= 750) {
        panel.style.height = '100vh';
      }
      panel.style.zIndex = '10';

      // Reduced motion: show all cards, no animation
      if (prefersReducedMotion) {
        track.style.height = 'auto';
        track.style.minHeight = 'auto';
        panel.style.position = 'relative';
        panel.style.height = 'auto';
        cards.forEach(function (c) {
          c.classList.add('story-active');
          c.style.position = 'relative';
          c.style.opacity = '1';
          c.style.transform = 'none';
          c.style.pointerEvents = 'auto';
          c.style.marginBottom = '20px';
        });
        return;
      }

      var currentIndex = -1;
      var ticking = false;

      function setActiveCard(idx) {
        if (idx === currentIndex) return;
        var prev = currentIndex;
        currentIndex = idx;
        cards.forEach(function (card, i) {
          card.classList.remove('story-active', 'story-exit-up');
          if (i === idx) {
            card.classList.add('story-active');
          } else if (i === prev && prev < idx) {
            card.classList.add('story-exit-up');
          }
        });
        dots.forEach(function (dot, i) {
          dot.classList.remove('active', 'done');
          if (i === idx) dot.classList.add('active');
          else if (i < idx) dot.classList.add('done');
        });
      }

      // Allow clicking dots to jump between cards
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          setActiveCard(i);
        });
      });

      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          // getBoundingClientRect is viewport-relative, works regardless of scroll container
          var rect = track.getBoundingClientRect();
          var trackH = rect.height;
          var vh = window.innerHeight;

          // How far track top has scrolled above viewport top
          var scrolled = -rect.top;

          if (scrolled < 0) {
            // Track hasn't reached viewport top yet
            setActiveCard(0);
            if (fill) fill.style.width = '0%';
            if (hint) hint.classList.remove('hidden');
            ticking = false;
            return;
          }

          var scrollRange = trackH - vh;
          if (scrollRange <= 0) {
            ticking = false;
            return;
          }

          if (scrolled >= scrollRange) {
            // Scrolled past the track
            setActiveCard(CARD_COUNT - 1);
            if (fill) fill.style.width = '100%';
            if (hint) hint.classList.add('hidden');
            ticking = false;
            return;
          }

          var progress = Math.max(0, Math.min(1, scrolled / scrollRange));

          // Update progress bar
          if (fill) fill.style.width = (progress * 100).toFixed(2) + '%';

          // Which card?
          var segment = 1 / CARD_COUNT;
          var activeIdx = Math.min(Math.floor(progress / segment), CARD_COUNT - 1);
          setActiveCard(activeIdx);

          // Scroll hint
          if (hint) {
            if (progress > 0.05) hint.classList.add('hidden');
            else hint.classList.remove('hidden');
          }

          ticking = false;
        });
      }

      // Init first card
      setActiveCard(0);

      // Listen on BOTH window, document, and .page-wrapper so all environments are caught
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('scroll', onScroll, { passive: true });
      var scrollContainer = document.querySelector('.page-wrapper');
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', onScroll, { passive: true });
      }
      window.addEventListener('resize', onScroll, { passive: true });

      // Run immediately
      onScroll();

    } catch (err) {
      // Non-critical — fails silently
    }
  }

  /* ─── 9. Interactive Luxury Testimonials Carousel ─── */
  function initTestimonialsCarousel() {
    try {
      var carousel = document.getElementById('zelvix-testimonials-carousel');
      if (!carousel) return;
      if (carousel.dataset.zelvixCarouselBound) return;
      carousel.dataset.zelvixCarouselBound = 'true';

      var track = carousel.querySelector('.zelvix-testimonials-track');
      var cards = Array.prototype.slice.call(carousel.querySelectorAll('.zelvix-review-card'));
      var prevBtn = carousel.querySelector('.zelvix-carousel-prev');
      var nextBtn = carousel.querySelector('.zelvix-carousel-next');
      var dotsContainer = carousel.querySelector('.zelvix-carousel-dots');

      if (!track || cards.length === 0) return;

      var totalCards = cards.length;
      var currentIndex = 0;
      var autoplayTimer = null;

      function getCardsPerPage() {
        if (window.innerWidth < 750) return 1;
        if (window.innerWidth < 990) return 2;
        return 3;
      }

      function getMaxIndex() {
        var perPage = getCardsPerPage();
        return Math.max(0, totalCards - perPage);
      }

      function renderDots() {
        if (!dotsContainer) return;
        dotsContainer.innerHTML = '';
        var maxIdx = getMaxIndex();
        var numDots = maxIdx + 1;
        if (numDots <= 1) return;

        for (var i = 0; i < numDots; i++) {
          (function (idx) {
            var dot = document.createElement('button');
            dot.className = 'zelvix-carousel-dot' + (idx === currentIndex ? ' active' : '');
            dot.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
            dot.setAttribute('type', 'button');
            dot.addEventListener('click', function () {
              goToSlide(idx);
              restartAutoplay();
            });
            dotsContainer.appendChild(dot);
          })(i);
        }
      }

      function updateSlidePosition() {
        var card = cards[0];
        if (!card) return;
        var cardWidth = card.offsetWidth;
        var gap = 24; // matches CSS gap
        var offset = currentIndex * (cardWidth + gap);
        track.style.transform = 'translateX(-' + offset + 'px)';

        // Update dots
        if (dotsContainer) {
          var dots = dotsContainer.querySelectorAll('.zelvix-carousel-dot');
          for (var i = 0; i < dots.length; i++) {
            if (i === currentIndex) {
              dots[i].classList.add('active');
            } else {
              dots[i].classList.remove('active');
            }
          }
        }

        // Update button states
        if (prevBtn) prevBtn.disabled = (currentIndex === 0);
        if (nextBtn) nextBtn.disabled = (currentIndex >= getMaxIndex());
      }

      function goToSlide(idx) {
        var maxIdx = getMaxIndex();
        currentIndex = Math.max(0, Math.min(idx, maxIdx));
        updateSlidePosition();
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          if (currentIndex > 0) {
            goToSlide(currentIndex - 1);
          } else {
            goToSlide(getMaxIndex());
          }
          restartAutoplay();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          if (currentIndex < getMaxIndex()) {
            goToSlide(currentIndex + 1);
          } else {
            goToSlide(0);
          }
          restartAutoplay();
        });
      }

      // Touch swipe support
      var startX = 0;
      var moveX = 0;
      track.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        moveX = startX;
        stopAutoplay();
      }, { passive: true });

      track.addEventListener('touchmove', function (e) {
        moveX = e.touches[0].clientX;
      }, { passive: true });

      track.addEventListener('touchend', function () {
        var diff = startX - moveX;
        if (Math.abs(diff) > 40) {
          if (diff > 0) {
            // swipe left -> next
            if (currentIndex < getMaxIndex()) goToSlide(currentIndex + 1);
            else goToSlide(0);
          } else {
            // swipe right -> prev
            if (currentIndex > 0) goToSlide(currentIndex - 1);
            else goToSlide(getMaxIndex());
          }
        }
        startAutoplay();
      }, { passive: true });

      // Autoplay with pause on hover
      function startAutoplay() {
        if (prefersReducedMotion) return;
        stopAutoplay();
        autoplayTimer = setInterval(function () {
          if (currentIndex < getMaxIndex()) {
            goToSlide(currentIndex + 1);
          } else {
            goToSlide(0);
          }
        }, 5500);
      }

      function stopAutoplay() {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      }

      function restartAutoplay() {
        stopAutoplay();
        startAutoplay();
      }

      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);

      window.addEventListener('resize', debounce(function () {
        renderDots();
        goToSlide(Math.min(currentIndex, getMaxIndex()));
      }, 150));

      renderDots();
      goToSlide(0);
      startAutoplay();

    } catch (err) {
      // Non-critical
    }
  }

  /* ─── 10. Master Initialization ─── */
  function initAll() {
    initBrandNameScrubber();
    initLivePersonalization();
    initUrgencyTimer();
    initScrollReveals();
    initCardTilt();
    initHeaderScroll();
    initParticles();
    initScrollStory();
    initTestimonialsCarousel();
  }

  // Safe DOM ready execution
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Handle Shopify Section reloads in Theme Editor safely with debouncing
  var debouncedReload = debounce(function () {
    initAll();
  }, 100);

  document.addEventListener('shopify:section:load', debouncedReload);
  document.addEventListener('shopify:section:select', debouncedReload);
})();
